import {
    useRef,
    useEffect,
    useCallback,
    MouseEvent as ReactMouseEvent,
} from "react";

import { DragPositionChange } from "@/utilities/types/draggable";


/**
 * @callback onDragStart
 * @param {MouseEvent} ev - mousedown event
 * @returns {void}
 *
 * @callback onDragMove
 * @param {MouseEvent} ev - mousemove event
 * @param {DragPositionChange} diffTotal - change in x/y in regards to mousedown location
 * @param {DragPositionChange} diffLast - change in x/y in regards to last mousemove location
 * @returns {void}
 *
 * @callback onDragEnd
 * @param {MouseEvent} ev - mouseup event
 * @returns {void}
 */
export interface DraggableProps extends Omit<React.HTMLProps<HTMLSpanElement>, "onDragEnd" | "onDragStart"> {
    children: React.ReactNode;
    onDragStart?: (ev: MouseEvent) => void;
    onDragMove?: (ev: MouseEvent, diffTotal: DragPositionChange, diffLast: DragPositionChange) => void;
    onDragEnd?: (ev: MouseEvent, diffTotal: DragPositionChange) => void;
}

export const Draggable: React.FC<DraggableProps> = ({
    children,
    onDragStart,
    onDragMove,
    onDragEnd,
    ...rest
}: DraggableProps) => {
    const refDraggable = useRef<HTMLDivElement>(null);
    const refMouseDown = useRef<MouseEvent>();
    const refLastMouseEvent = useRef<MouseEvent>();
    const refCallbacks = useRef<Omit<DraggableProps, "children">>({
        onDragStart,
        onDragMove,
        onDragEnd,
    });

    useEffect(() => {
        refCallbacks.current = {
            onDragStart,
            onDragMove,
            onDragEnd,
        };
    }, [onDragStart, onDragMove, onDragEnd]);

    const onMouseMove = useCallback((ev: MouseEvent) => {
        const onDragMove = refCallbacks.current.onDragMove;

        if (onDragMove && refDraggable.current && refLastMouseEvent.current && refMouseDown.current) {
            const boundingBox =
                refDraggable.current.getBoundingClientRect();
            const diffLast = computePositionChange(ev, refLastMouseEvent.current, boundingBox);
            const diffTotal = computePositionChange(ev, refMouseDown.current, boundingBox);

            refLastMouseEvent.current = ev;
            onDragMove(ev, diffTotal, diffLast);
        }
    }, []);

    const onMouseUp = useCallback((ev: MouseEvent) => {
        const onDragEnd = refCallbacks.current.onDragEnd;

        refLastMouseEvent.current = undefined;

        if (onDragEnd && refMouseDown.current && refDraggable.current) {
            const boundingBox =
                refDraggable.current.getBoundingClientRect();
            const diffTotal = computePositionChange(ev, refMouseDown.current, boundingBox);

            onDragEnd(ev, diffTotal);
        }

        window.removeEventListener("mouseup", onMouseUp);
        window.removeEventListener("mousemove", onMouseMove);
    }, []);

    const onMouseDown = useCallback((syntheticEv: ReactMouseEvent<HTMLDivElement>) => {
        const onDragStart = refCallbacks.current.onDragStart;
        const ev = syntheticEv.nativeEvent;

        refMouseDown.current = refLastMouseEvent.current = ev;

        if (onDragStart) {
            onDragStart(ev);
        }

        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
    }, []);

    return (
        <span ref={refDraggable} onMouseDown={onMouseDown} {...rest}>
            {children}
        </span>
    );
};

const computePositionChange = (
    nextEv: MouseEvent,
    previousEv: MouseEvent,
    rect: DOMRect,
): DragPositionChange => {
    const diffX = nextEv.clientX - previousEv.clientX;
    const diffY = nextEv.clientY - previousEv.clientY;
    const diffXRelative = diffX / rect.width;
    const diffYRelative = diffY / rect.height;

    return { diffX, diffY, diffXRelative, diffYRelative };
};
